import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardActionArea,
  Paper
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import EditNoteIcon from '@mui/icons-material/EditNote';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import InventoryIcon from '@mui/icons-material/Inventory';
import ScienceIcon from '@mui/icons-material/Science';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ConstructionIcon from '@mui/icons-material/Construction';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';

// Dashboard Home Component with Shortcut Cards
const MahasiswaDashboardHome = () => {
  const [userName, setUserName] = useState('Pengguna');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userRef = doc(db, 'users', user.uid);
        try {
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            setUserName(userDoc.data().nama || user.email || 'Pengguna');
          } else {
            setUserName(user.email || 'Pengguna');
          }
        } catch (error) {
          console.error("Error fetching user name:", error);
          setUserName(user.email || 'Pengguna');
        }
      } else {
        setUserName('Pengguna');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Define shortcut cards with priority (most important first)
  const shortcutCards = [
    {
      title: 'Scan Alat',
      description: 'Scan QR code untuk menggunakan alat',
      icon: <QrCodeScannerIcon />,
      path: '/scan-alat',
      color: '#1976d2',
      priority: 'high'
    },
    {
      title: 'Form Peminjaman Alat',
      description: 'Ajukan permintaan peminjaman alat',
      icon: <EditNoteIcon />,
      path: 'form-peminjaman-alat',
      color: '#2e7d32',
      priority: 'high'
    },
    {
      title: 'Status Permintaan',
      description: 'Cek status pengajuan Anda',
      icon: <PlaylistAddCheckIcon />,
      path: 'status-permintaan',
      color: '#ed6c02',
      priority: 'high'
    },
    {
      title: 'Form Permintaan Bahan',
      description: 'Ajukan permintaan bahan',
      icon: <EditNoteIcon />,
      path: 'form-permintaan-bahan',
      color: '#2e7d32',
      priority: 'medium'
    },
    {
      title: 'Status Penggunaan Alat',
      description: 'Lihat alat yang sudah disetujui',
      icon: <ConstructionIcon />,
      path: 'alat-siap-digunakan',
      color: '#9c27b0',
      priority: 'medium'
    },
    {
      title: 'Katalog Instrumen',
      description: 'Lihat daftar instrumen tersedia',
      icon: <InventoryIcon />,
      path: 'katalog-instrumen',
      color: '#1565c0',
      priority: 'medium'
    },
    {
      title: 'Katalog Alat Gelas',
      description: 'Lihat daftar alat gelas tersedia',
      icon: <ScienceIcon />,
      path: 'katalog-alat-gelas',
      color: '#1565c0',
      priority: 'low'
    },
    {
      title: 'Katalog Bahan',
      description: 'Lihat daftar bahan tersedia',
      icon: <ScienceIcon />,
      path: 'katalog-bahan',
      color: '#1565c0',
      priority: 'low'
    },
    {
      title: 'Jadwal Penggunaan',
      description: 'Lihat jadwal penggunaan alat',
      icon: <CalendarTodayIcon />,
      path: 'jadwal-penggunaan',
      color: '#7b1fa2',
      priority: 'low'
    },
    {
      title: 'Profil',
      description: 'Edit profil Anda',
      icon: <AccountCircleIcon />,
      path: 'edit-profil',
      color: '#616161',
      priority: 'low'
    },
  ];

  const handleCardClick = (path) => {
    if (path.startsWith('/')) {
      navigate(path);
    } else {
      navigate(path);
    }
  };

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 2, md: 3 },
      maxWidth: '1400px',
      mx: 'auto',
      width: '100%',
      boxSizing: 'border-box'
    }}>
      {/* Welcome Section with Logo */}
      <Box 
        sx={{ 
          mb: { xs: 1.5, sm: 3 },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          gap: { xs: 1, sm: 2 }
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography 
            variant="h5" 
            gutterBottom 
            sx={{ 
              fontWeight: 600,
              fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2rem' },
              mb: { xs: 0.25, sm: 0.5 },
              lineHeight: 1.2
            }}
          >
        Selamat datang, {loading ? '...' : userName}!
      </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary" 
            sx={{ 
              fontSize: { xs: '0.8125rem', sm: '1rem' },
              lineHeight: 1.3
            }}
          >
            Sistem Manajemen Laboratorium Rekayasa Kosmetik
      </Typography>
        </Box>

        {/* Logo - Top Right */}
        <Box 
          sx={{ 
            display: 'block',
            flexShrink: 0
          }}
        >
          <Box
            component="img"
            src="/images/LOGO PRODI REKOS.jpg"
          alt="Logo Prodi Rekayasa Kosmetik"
            sx={{
              maxWidth: { xs: '80px', sm: '120px' },
              height: 'auto',
              maxHeight: { xs: 50, sm: 80 },
              borderRadius: 1,
              display: 'block'
            }}
          />
        </Box>
      </Box>

      {/* Shortcut Cards Section */}
      <Box>
        <Typography 
          variant="h6" 
          gutterBottom 
          sx={{ 
            fontWeight: 600,
            fontSize: { xs: '1rem', sm: '1.25rem' },
            mb: { xs: 1.5, sm: 2.5 },
            color: 'text.primary'
          }}
        >
          Menu Cepat
        </Typography>
        
        <Grid container spacing={{ xs: 1, sm: 2, md: 2.5 }}>
          {shortcutCards.map((card, index) => (
            <Grid item xs={6} sm={4} md={3} key={index} sx={{ display: 'flex' }}>
              <Card 
                elevation={0}
                sx={{ 
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: { xs: 1.5, sm: 2 },
                  border: card.priority === 'high' 
                    ? `2px solid ${card.color}` 
                    : '1px solid rgba(0, 0, 0, 0.12)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  backgroundColor: 'background.paper',
                  '&:hover': {
                    transform: { xs: 'none', sm: 'translateY(-6px)' },
                    boxShadow: { xs: 2, sm: 6 },
                    borderColor: card.color,
                    '& .card-icon': {
                      transform: { xs: 'none', sm: 'scale(1.1)' }
                    }
                  }
                }}
              >
                <CardActionArea 
                  onClick={() => handleCardClick(card.path)}
                  sx={{ 
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    p: { xs: 1, sm: 2 },
                    minHeight: { xs: 120, sm: 150, md: 160 },
                    width: '100%',
                    '&:hover': {
                      backgroundColor: 'transparent'
                    }
                  }}
                >
                  <Box 
                    className="card-icon"
                    sx={{ 
                      color: card.color,
                      mb: { xs: 0.75, sm: 1.5 },
                      transition: 'transform 0.3s ease',
                      fontSize: { xs: 32, sm: 40, md: 44 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {card.icon}
                  </Box>
                  <CardContent 
                    sx={{ 
                      textAlign: 'center', 
                      p: 0,
                      pt: 0,
                      flexGrow: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      width: '100%',
                      minWidth: 0
                    }}
                  >
                    <Typography 
                      variant="subtitle1" 
                      component="div" 
                      sx={{ 
                        fontWeight: 600,
                        fontSize: { xs: '0.8125rem', sm: '0.9375rem', md: '1rem' },
                        mb: { xs: 0, sm: 0.75 },
                        lineHeight: 1.3,
                        color: 'text.primary',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word'
                      }}
                    >
                      {card.title}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
                        display: { xs: 'none', sm: 'block' },
                        lineHeight: 1.4,
                        px: 0.5,
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word'
                      }}
                    >
                      {card.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default MahasiswaDashboardHome;
